import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiLongRunningRequestTimeoutMs } from "../config";

import useApiHelper from "../hooks/useApiHelper";
import { IAccount } from "../models/account/IAccount";
import { IAccountMergePreview, IDuplicateReport, IDuplicateTidyResult } from "../models/account/IDuplicates";
import { IContact } from "../models/Contact";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";
import { withoutGroup } from "../utils/contactDuplicates";

export interface IMergeRequest {
  targetId: string;
  sourceIds: string[];
  // Accounts only: whose bank details the kept account ends up with.
  bankDetailsFromAccountId?: string;
}

// The admin clean-up for people who exist more than once: the report, the tidy of provably empty
// duplicates, and the hand merges for the ones with history. All admin-only on the API.
export default function useAccountDuplicates(enabled = true, componentName = "useAccountDuplicates") {
  // The report and the tidy are built live over every contact: on a big customer they take longer
  // than the default request timeout.
  const { get, post } = useApiHelper({ ...apiConfig(componentName), timeoutMs: apiLongRunningRequestTimeoutMs });
  const queryClient = useQueryClient();

  // Everything a merge or tidy can move: accounts, contacts, the leads and invoices on them.
  const invalidateAll = () => {
    queryClient.invalidateQueries(["accounts"]);
    queryClient.invalidateQueries(["contacts"]);
    queryClient.invalidateQueries(["leads"]);
    queryClient.invalidateQueries(["invoices"]);
    // The contact page and lead screens: who a lead is on, and the merge badges and history.
    queryClient.invalidateQueries(["contact"]);
    queryClient.invalidateQueries(["contactLeads"]);
    queryClient.invalidateQueries(["contactMerges"]);
    queryClient.invalidateQueries(["contactDocuments"]);
    queryClient.invalidateQueries(["leadsDocuments"]);
    queryClient.invalidateQueries(["lead"]);
  };

  const duplicates = useQuery({
    queryKey: ["accounts", "duplicates"],
    queryFn: () => get<DefaultResponseWithData<IDuplicateReport>>("/account/duplicates"),
    enabled,
    // Always the live picture - it has to reflect the tidy or merge just made.
    staleTime: 0,
    // A slow report retried three times just keeps the spinner going for minutes.
    retry: false,
  });

  // After a merge: take that person out of the report on screen rather than building it again, which
  // on a big customer takes minutes.
  const dropGroup = (keptContactId: string) =>
    queryClient.setQueryData<DefaultResponseWithData<IDuplicateReport>>(["accounts", "duplicates"], (current) =>
      current?.data ? { ...current, data: withoutGroup(current.data, keptContactId) } : current
    );

  const tidy = useMutation({
    mutationFn: (dryRun: boolean) =>
      post<object, DefaultResponseWithData<IDuplicateTidyResult>>(`/account/duplicates/tidy?dryRun=${dryRun}`, {}),
    onSuccess: (_result, dryRun) => {
      if (!dryRun) invalidateAll();
    },
  });

  const mergeAccounts = useMutation({
    mutationFn: ({ targetId, sourceIds, bankDetailsFromAccountId }: IMergeRequest) =>
      post<object, DefaultResponseWithData<IAccount>>(`/account/${targetId}/merge`, {
        sourceAccountIds: sourceIds,
        bankDetailsFromAccountId,
      }),
    onSuccess: invalidateAll,
  });

  const mergeContacts = useMutation({
    mutationFn: ({ targetId, sourceIds }: IMergeRequest) =>
      post<object, DefaultResponseWithData<IContact>>(`/contact/${targetId}/merge`, { sourceContactIds: sourceIds }),
    onSuccess: invalidateAll,
  });

  return {
    duplicates,
    tidy,
    mergeAccounts,
    mergeContacts,
    dropGroup,
  };
}

// Every account in a merge side by side before anything moves. Built live, so never cached; a refetch
// would reset the admin's choices in the dialog.
export function useAccountMergePreview(keptId: string | null, sourceIds: string[], componentName = "useAccountMergePreview") {
  const { get } = useApiHelper(apiConfig(componentName));
  const query = sourceIds.map((id) => `sourceIds=${id}`).join("&");

  return useQuery({
    queryKey: ["accounts", "mergePreview", keptId, ...sourceIds],
    queryFn: () => get<DefaultResponseWithData<IAccountMergePreview>>(`/account/${keptId}/merge-preview?${query}`),
    enabled: !!keptId && sourceIds.length > 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
